import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  Polyline,
  useJsApiLoader,
  Autocomplete,
  InfoWindow,
  OverlayView
} from '@react-google-maps/api';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  InputBase,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse
} from '@mui/material';
import EditVehicleModal from './edit-vehicle-modal.tsx';
import TripsListDrawer from './trip-list-card.tsx';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NearMeIcon from '@mui/icons-material/NearMe';
import CircleIcon from '@mui/icons-material/Circle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FileClockOutline from 'mdi-material-ui/FileClockOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CallIcon from '@mui/icons-material/Call';
import { FaFilePen } from "react-icons/fa6";
import HandshakeIcon from '@mui/icons-material/Handshake';
import mapsIcon  from '../../../assets/map.png';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Draggable from 'react-draggable';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { toast } from 'react-toastify';
import { getCurrentTrip, getVehicleRoute, getCurrentLocation, getVehicle } from './action.ts';
import TripDetailsModal from './trip-details-modal.tsx';

const containerStyle = { width: '100%', height: '100vh' };
const libraries: ("places" | "geometry")[] = ['places', 'geometry'];
const DEFAULT_CENTER = { lat:  12.834932, lng: 77.606037 };

const getDistanceInMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  if (!window.google) return Infinity;
  const p1 = new window.google.maps.LatLng(lat1, lng1);
  const p2 = new window.google.maps.LatLng(lat2, lng2);
  return window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
};

const processAndFilterStops = (engineEvents: any[] = [], idleEvents: any[] = []) => {
  const engineStops = engineEvents
    .filter((event: any) => event.type === "stop")
    .map((event: any) => ({
      lat: Number(event.lat),
      lng: Number(event.lng),
      timestamp: event.timestamp || "",
      type: "engine"
    }));

  const idleStops = idleEvents
    .map((event: any) => ({
      lat: Number(event.start_latitude || event.lat),
      lng: Number(event.start_longitude || event.lng),
      timestamp: event.start_time || event.timestamp || "",
      type: "idle"
    }));

  const combinedStops = [...engineStops, ...idleStops];
  const filteredStops: any[] = [];

  combinedStops.forEach((current) => {
    const isTooClose = filteredStops.some((kept) => {
      const dist = getDistanceInMeters(current.lat, current.lng, kept.lat, kept.lng);
      return dist < 100; 
    });
    if (!isTooClose) {
      filteredStops.push(current);
    }
  });

  return filteredStops;
};

interface SharedGoogleMapProps {
  mapContainerStyle?: React.CSSProperties;
  mapCenter: { lat: number; lng: number };
  zoom?: number;
  options?: any;
  onLoad?: (map: google.maps.Map) => void;
  directions?: google.maps.DirectionsResult | null;
  showPolyline?: boolean;
  tripPath?: { lat: number; lng: number }[];
  tripStops?: { lat: number; lng: number; timestamp: string; type?: string }[];
  tripStartPos?: { lat: number; lng: number } | null;
  tripEndPos?: { lat: number; lng: number } | null;
  originPos?: { lat: number; lng: number } | null;
  destPos?: { lat: number; lng: number } | null;
  currentRoutePos?: { lat: number; lng: number } | null;
}

const SharedGoogleMap: React.FC<SharedGoogleMapProps> = ({
  mapContainerStyle = containerStyle,
  mapCenter,
  zoom = 13,
  options,
  onLoad,
  directions,
  showPolyline,
  tripPath = [],
  tripStops = [],
  tripStartPos,
  tripEndPos,
  originPos,
  destPos,
  currentRoutePos
}) => {
  const [selectedStop, setSelectedStop] = useState<any>(null);

  const getArrowRotation = () => {
    if (!window.google) return 0;

    if (directions && directions.routes[0]?.overview_path?.length > 1) {
      return window.google.maps.geometry.spherical.computeHeading(
        directions.routes[0].overview_path[0],
        directions.routes[0].overview_path[1]
      );
    }

    if (tripPath && tripPath.length > 1) {
      return window.google.maps.geometry.spherical.computeHeading(
        new window.google.maps.LatLng(tripPath[0].lat, tripPath[0].lng),
        new window.google.maps.LatLng(tripPath[1].lat, tripPath[1].lng)
      );
    }

    return 0;
  };

  const finalDestPos = showPolyline ? tripEndPos : destPos;
  const finalStartPos = showPolyline ? tripStartPos : originPos;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={zoom}
      onLoad={onLoad}
      options={options}
    >
      {directions && !showPolyline && (
        <DirectionsRenderer
          directions={directions}
          options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#1a73e8', strokeWeight: 6 } }}
        />
      )}

      {showPolyline && (
        <>
          <Polyline
            path={tripPath}
            options={{ strokeColor: '#1a73e8', strokeOpacity: 1, strokeWeight: 5 }}
          />
          
          {tripStops.map((stop, index) => (
            <Marker
              key={`stop-${index}`}
              position={{ lat: stop.lat, lng: stop.lng }}
              onClick={() => setSelectedStop({ ...stop, index })}
              onMouseOver={() => setSelectedStop({ ...stop, index })}
              label={{ text: (index + 1).toString(), color: "white", fontSize: "12px", fontWeight: "bold" }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: stop.type === "engine" ? "#d32f2f" : "#f18d12",
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: "white",
              }}
            />
          ))}

          {selectedStop && (
            <InfoWindow
              position={{ lat: selectedStop.lat, lng: selectedStop.lng }}
              onCloseClick={() => setSelectedStop(null)}
              options={{ pixelOffset: new window.google.maps.Size(0, -10), disableAutoPan: true }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: "120px", px: 1, py: 0.5 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {new Date(selectedStop?.timestamp?.replace(" ", "T")).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
                </Typography>
                <IconButton size="small" onClick={() => setSelectedStop(null)} sx={{ ml: 1, p: "2px" }}>✕</IconButton>
              </Box>
            </InfoWindow>
          )}
        </>
      )}

      {finalStartPos && (
        <Marker 
          position={finalStartPos} 
          title="Start Location" 
          icon={{ 
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, 
            scale: 6, 
            fillColor: "#1a73e8", 
            fillOpacity: 1, 
            strokeWeight: 2, 
            strokeColor: "#FFFFFF", 
            rotation: getArrowRotation() 
          }} 
        />
      )}

      {finalDestPos && (
        <OverlayView
          position={finalDestPos}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={() => ({ x: -20, y: -20 })}
        >
          <div style={{ 
            position: 'relative', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '40px', 
            height: '40px'
          }}>
            <div style={{
              position: 'absolute',
              width: '16px',
              height: '16px',
              backgroundColor: '#d93025', 
              borderRadius: '50%',
              animation: 'location-pulse 1.5s infinite ease-out',
              boxSizing: 'border-box'
            }} />
            <div style={{
              position: 'absolute',
              width: '16px',
              height: '16px',
              backgroundColor: '#d93025',
              borderRadius: '50%',
              boxShadow: '0 0 4px rgba(0,0,0,0.4)',
              zIndex: 2,
              boxSizing: 'border-box'
            }} />
          </div>
        </OverlayView>
      )}
    </GoogleMap>
  );
};

const TripDirections: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [state] = useState<any>(() => {
    if (location.state) {
      return location.state;
    }
    
    const stateKey = searchParams.get('stateKey');
    if (stateKey) {
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        try {
          return JSON.parse(storedState);
        } catch (e) {
          console.error("Failed to parse state from local storage");
        }
      }
    }
    return null;
  });
  
  const userData = JSON.parse(localStorage.getItem('userdetails') || 'null');
  const isClientCompanyAdmin = userData?.role === 'client_company_admin';
  const loggedInCompaniesId = userData?.companies_id;
  const vehicleCompaniesId = state?.companies_id || state?.vehicleList?.find((v: any) => String(v.id) === String(state?.vehiclesId))?.companies_id;
  const canEditVehicle = String(loggedInCompaniesId) === String(vehicleCompaniesId);
  
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [originPos, setOriginPos] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
  const [destPos, setDestPos] = useState<{ lat: number; lng: number } | null>(null);
  const [tripPath, setTripPath] = useState<{ lat: number; lng: number }[]>([]);
  const [tripStartPos, setTripStartPos] = useState<{ lat: number; lng: number } | null>(null);
  const [tripEndPos, setTripEndPos] = useState<{ lat: number; lng: number } | null>(null);
  const [currentRoutePos, setCurrentRoutePos] = useState<{ lat: number; lng: number } | null>(null);
  const [tripStops, setTripStops] = useState<{ lat: number; lng: number; timestamp: string }[]>([]);
  const [showPolyline, setShowPolyline] = useState(false);
  const [dayWiseData, setDayWiseData] = useState<any[] | null>(null);

  const getDefaultTimes = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return {
      start: `${year}-${month}-${day}T00:00`,
      end: `${year}-${month}-${day}T23:59`
    };
  };

  const defaultTimes = getDefaultTimes();
  const [fromDate, setFromDate] = useState<string>(defaultTimes.start);
  const [toDate, setToDate] = useState<string>(defaultTimes.end);
    
  const [showDatePicker, setShowDatePicker] = useState(true);
  const [viewMode, setViewMode] = useState<'map' | 'history' | 'trips'>('history');
    
  const [openTripsDrawer, setOpenTripsDrawer] = useState(false);
  const [originText, setOriginText] = useState('');
  const [destText, setDestText] = useState('');
  const [distance, setDistance] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [isTripOngoing, setIsTripOngoing] = useState(false);
  const [currentTripData, setCurrentTripData] = useState<any>(null);
  const [openTripModal, setOpenTripModal] = useState(false);
  const [tripActionType, setTripActionType] = useState<'start' | 'end' | 'handover'>('start');
  const [fetchedVehicle, setFetchedVehicle] = useState<any>(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const originAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const destAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc', 
    libraries,
  });

  const fetchVehicleDetails = () => {
    if (!state?.vehicleNo) return;
    let filterPayload: any = {};
    if (state?.activeMobileFilter === 'Absent') {
        filterPayload = { absent: 1 };
    } else if (state?.activeMobileFilter === 'Inactive') {
        filterPayload = { active: 0 };
    }
    setLoadingVehicle(true);
    getVehicle(
      '',
      '',
      (response) => {
        const vehicle = response?.message?.vehicles_list[0];
        setFetchedVehicle(vehicle);
        setLoadingVehicle(false);
      },
      (err) => {
        toast.error("Could not fetch vehicle details");
        setLoadingVehicle(false);
      },
      filterPayload,
      state.vehicleNo
    );
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .gm-ui-hover-effect {
        display: none !important;
      }
      @keyframes location-pulse {
        0% { transform: scale(1); opacity: 0.8; }
        100% { transform: scale(3.5); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const formatDateDDMMYYYY = (dateString: string) => {
      if (!dateString) return '';
      const parts = dateString.split('-');
      if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateString;
  };

  useEffect(() => {
    if (!state?.vehiclesId) return;

    getCurrentLocation(
      (response: any) => {
        if (response.status === 200 && response.data && response.data.length > 0) {
          const locationData = response.data[0];
          const lat = Number(locationData.lat);
          const lng = Number(locationData.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            const newDest = { lat, lng };
            setDestText(`${lat}, ${lng}`);
            setDestPos(newDest);
            setMapCenter(newDest); 
          }
        }
      },
      (errMessage: any) => {
        toast.error(typeof errMessage === 'string' ? errMessage : "Failed to update location");
      },
      [state.vehiclesId]
    );
  }, [state?.vehiclesId]);

  const fetchCurrentTrip = () => {
    getCurrentTrip(
      (response: any) => {
        if (response?.status === 404 || response?.message?.errors) {
              setCurrentTripData(null);
              setIsTripOngoing(false);
        } else {
              const trip = response?.message;
              setCurrentTripData(trip);
              const statusLower = trip?.status?.toLowerCase() || '';
              setIsTripOngoing(statusLower.includes('started') || statusLower === 'handover');
        }
      },
      () => {
        setIsTripOngoing(false);
        setCurrentTripData(null);
      },
      '',
      state?.vehiclesId
    );
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
  };

  const handleEditClick = (vehicle: any) => {
    fetchVehicleDetails();
    setOpenEditModal(true);
  };

  const handleFetchTripRoute = (startStr: string, endStr: string) => {
    if (!state?.vehiclesId) return toast.error("Vehicle ID not found");

    setTripStartPos(null);
    setTripEndPos(null);
    setCurrentRoutePos(null);
    setTripPath([]);
    setTripStops([]);
    setDayWiseData(null);

    const formatForApi = (dateStr: string) => {
      if (!dateStr) return '';
      return dateStr.replace('T', ' ') + ':00';
    };

    const startDateTime = formatForApi(startStr);
    const endDateTime = formatForApi(endStr);

    const resetToCurrentLocation = () => {
      setTripPath([]);
      setTripStops([]);
      setShowPolyline(false);
      setDirections(null);
      const fallbackPos = destPos || originPos || DEFAULT_CENTER;
      setMapCenter(fallbackPos);

      if (map && window.google) {
        map.panTo(fallbackPos);
        map.setZoom(15);
      }
    };

    getVehicleRoute(
      state.vehiclesId,
      startDateTime,
      endDateTime,
      (response: any) => {
        const routeData = response?.data || response;

      if (routeData?.day_wise_distance && Array.isArray(routeData.day_wise_distance)) {
          const filteredDayWise = routeData.day_wise_distance.filter(
            (day: any) => Number(day.distance_km) >= 1
          );
          
          setDayWiseData(filteredDayWise);
          setShowPolyline(false);
          return;
        }

        if (routeData) {
          if (routeData.distance_km !== undefined) {
            setDistance(`${Number(routeData.distance_km).toFixed(2)} km`);
          }
          if (routeData.time_taken) {
            const { seconds, hours } = routeData.time_taken;
            if (seconds) {
              const h = Math.floor(Number(seconds) / 3600);
              const m = Math.floor((Number(seconds) % 3600) / 60);
              setDuration(`${h}h ${m}m`);
            } else if (hours) {
              setDuration(`${Number(hours).toFixed(1)} hrs`);
            }
          }
        }

        if (routeData?.start?.lat && routeData?.start?.lng) {
          setTripStartPos({ lat: Number(routeData.start.lat), lng: Number(routeData.start.lng) });
        }
        if (routeData?.end?.lat && routeData?.end?.lng) {
          setTripEndPos({ lat: Number(routeData.end.lat), lng: Number(routeData.end.lng) });
        }
        if (routeData?.current_location?.lat && routeData?.current_location?.lng) {
          setCurrentRoutePos({ lat: Number(routeData.current_location.lat), lng: Number(routeData.current_location.lng) });
        }

        if (routeData) {
          const filteredStops = processAndFilterStops(routeData.engine_events, routeData.idle_events);
          setTripStops(filteredStops);
        }

        if (routeData?.polyline && window.google?.maps) {
          try {
            const decodedPath = window.google.maps.geometry.encoding.decodePath(routeData.polyline);
            const formattedPath = decodedPath.map((latLng) => ({
              lat: latLng.lat(),
              lng: latLng.lng()
            }));

            setTripPath(formattedPath);
            setShowPolyline(true);
            setDirections(null);

            if (map) {
              const bounds = new window.google.maps.LatLngBounds();
              formattedPath.forEach((p) => bounds.extend(p));
              setTripStops((prev) => {
                prev.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
                return prev;
              });
              map.fitBounds(bounds);
            }
          } catch (error) {
            console.error("Error decoding polyline:", error);
          }
        } else {
          if (!routeData?.day_wise_distance) {
            toast.info("No route data found.");
            resetToCurrentLocation();
          }
        }
      },
      (error: any) => {
        const msg = error?.details?.message || error?.message || "Error fetching route";
        toast.error(msg);
        resetToCurrentLocation();
      }
    );
  };

  const handleMapClick = () => {
    setViewMode('map');
    setShowPolyline(false); 
    setTripStops([]); 
    setShowDatePicker(false);
    setOpenTripsDrawer(false); 
    setDayWiseData(null); 
    setDistance('');
    setDuration('');
    if (originPos) {
        setMapCenter(originPos); 
        
        if (map && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(originPos);
            if (destPos) bounds.extend(destPos);
            map.fitBounds(bounds);
        }
    }
  };

  const handleHistoryClick = () => {
    setViewMode('history');
    setShowDatePicker(true); 
    setOpenTripsDrawer(false);
    handleFetchTripRoute(fromDate, toDate); 
  };

  const handleTripsClick = () => {
    setViewMode('trips');
    setOpenTripsDrawer(true);
    setShowDatePicker(false); 
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setFromDate(newDate);
    handleFetchTripRoute(newDate, toDate);
    event.target.blur(); 
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setToDate(newDate);
    handleFetchTripRoute(fromDate, newDate);
    event.target.blur();
  };

  const handleRowClick = (dateStr: string) => {
    if (!state?.vehiclesId) return toast.error("Vehicle ID not found");
    
    const startDateTime = `${dateStr} 00:00:00`;
    const endDateTime = `${dateStr} 23:59:59`;

    getVehicleRoute(
      state.vehiclesId,
      startDateTime,
      endDateTime,
      (response: any) => {
        const routeData = response?.data || response;
        
        if (routeData) {
            if (routeData.start?.lat && routeData.start?.lng) {
                setTripStartPos({ lat: Number(routeData.start.lat), lng: Number(routeData.start.lng) });
            } else { setTripStartPos(null); }

            if (routeData.end?.lat && routeData.end?.lng) {
                setTripEndPos({ lat: Number(routeData.end.lat), lng: Number(routeData.end.lng) });
            } else { setTripEndPos(null); }

            if (routeData.current_location?.lat && routeData.current_location?.lng) {
                setCurrentRoutePos({ lat: Number(routeData.current_location.lat), lng: Number(routeData.current_location.lng) });
            } else { setCurrentRoutePos(null); }

            if (routeData.engine_events || routeData.idle_events) {
                const filteredStops = processAndFilterStops(routeData.engine_events, routeData.idle_events);
                setTripStops(filteredStops);
            } else {
                setTripStops([]);
            }

            if (routeData.polyline && window.google && window.google.maps) {
                try {
                    const decodedPath = window.google.maps.geometry.encoding.decodePath(routeData.polyline);
                    const formattedPath = decodedPath.map((latLng) => ({
                        lat: latLng.lat(), lng: latLng.lng()
                    }));
                    
                    setTripPath(formattedPath);
                    setShowPolyline(true);
                    setDirections(null); 
                    
                } catch (error) {
                    console.error("Error decoding polyline:", error);
                }
            } else {
                setTripPath([]);
                setShowPolyline(false);
            }
        } else {
            setTripPath([]);
            setShowPolyline(false);
            setTripStops([]);
            setTripStartPos(null);
            setTripEndPos(null);
            setCurrentRoutePos(null);
        }
      },
      (error: any) => toast.error("Error fetching route for selected day")
    );
  };

  const handleInputClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const input = e.target as HTMLInputElement;
      if (input.tagName === 'INPUT' && 'showPicker' in input) {
          try {
              (input as any).showPicker();
          } catch (error) {
              console.log("Picker not supported programmatically");
          }
      }
  };

  useEffect(() => { if (state) fetchCurrentTrip(); }, [openEditModal]);

  useEffect(() => {
    if (!state) { 
        navigate(-1); 
        return; 
    }
    if (isLoaded && window.google) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const currentPos = { lat: position.coords.latitude, lng: position.coords.longitude };          
          setOriginPos(currentPos);
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: currentPos }, (results, status) => {
            if (status === 'OK' && results?.[0]) setOriginText(results[0].formatted_address);
          });
          if (viewMode !== 'history') {
             setMapCenter(currentPos);
          }
        });
      }
    }
  }, [isLoaded, state]);

  useEffect(() => {
    if (isLoaded && viewMode === 'history' && map) {
      handleFetchTripRoute(fromDate, toDate);
    }
  }, [isLoaded, map]);

  useEffect(() => {
    if (viewMode === 'map' && isLoaded && originPos && destPos && directionsServiceRef.current && !showPolyline && !dayWiseData) {
      directionsServiceRef.current.route(
        {
          origin: originPos,
          destination: destPos,
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === 'OK' && result) {
            setDirections(result);
            const leg = result.routes[0]?.legs[0];
            if (leg) {
              setDistance(leg.distance?.text || '');
              setDuration(leg.duration?.text || '');
            }
          }
        }
      );
    }
  }, [originPos, destPos, isLoaded, showPolyline, viewMode, dayWiseData]);

  const buttonStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textTransform: 'none',
    padding: '8px 12px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    '& .MuiButton-startIcon': {
        marginRight: 0,
        marginLeft: 0
    }
  };

  const getActiveStyle = (mode: string) => {
      const isActive = viewMode === mode;
      return {
          ...buttonStyle,
          borderColor: isActive ? '#1a73e8' : '#5f6368',
          color: isActive ? '#1a73e8' : '#5f6368',
          bgcolor: isActive ? '#e8f0fe' : 'transparent',
          '&:hover': {
              bgcolor: isActive ? '#e8f0fe' : '#f5f5f5'
          }
      };
  };

  const noCalendarIconStyle = {
    '& input::-webkit-calendar-picker-indicator': {
      display: 'none',
      WebkitAppearance: 'none',
    },
    '& input': {
        cursor: 'pointer'
    }
  };

  const renderTripActionButton = () => {
    if (!currentTripData ) {
        return (
            <Button 
                variant="contained" 
                onClick={() => { 
                    setTripActionType('start'); 
                    setOpenTripModal(true); 
                }} 
                startIcon={<PlayArrowIcon />} 
                sx={{ ...buttonStyle, bgcolor: '#2e7d32' }}
            >
                Start Trip
            </Button>
        );
    }

    const status = currentTripData.status;
    const initiatedBy = currentTripData.initiated_by;
    const isHandover = status === 'handover';
    const isDriver = initiatedBy === 'driver' || !initiatedBy;
    const isVendor = initiatedBy === 'vendor_supervisor' || initiatedBy === 'vendor_company_admin';

    if (isClientCompanyAdmin && isHandover) {
        return (
            <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'orange', color: 'white' }} size="small" 
                startIcon={<HandshakeIcon />}
                onClick={() => { 
                    setTripActionType('handover'); 
                    setOpenTripModal(true); 
                }}>
                Handover Accept
            </Button>
        );
    }

    if (!isClientCompanyAdmin) {
        if (isHandover) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'orange', color: 'white' }} size="small" 
                    startIcon={<HandshakeIcon />}
                    onClick={() => { 
                        setTripActionType('handover'); 
                        setOpenTripModal(true); 
                    }}>
                    Handover Accept
                </Button>
            );
        }
        if (isDriver) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'red', color: 'white' }} size="small" 
                    startIcon={<StopIcon />}
                    onClick={() => { 
                        setTripActionType('end'); 
                        setOpenTripModal(true); 
                    }}>
                    End Trip
                </Button>
            );
        }
        if (isVendor) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: '#ef6363', color: 'white' }} size="small" 
                    startIcon={<StopIcon />}
                    onClick={() => { 
                        setTripActionType('end'); 
                        setOpenTripModal(true); 
                    }}>
                    End Trip
                </Button>
            );
        }
        return (
            <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'purple', color: 'white' }} size="small" 
                startIcon={<PriorityHighIcon />}
                onClick={() => { 
                    setTripActionType('end');
                    setOpenTripModal(true); 
                }}>
                Forced Handover
            </Button>
        );
    }
    return (
        <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'red', color: 'white' }} size="small" onClick={() => { setTripActionType('end'); setOpenTripModal(true); }}>
            End Trip
        </Button>
    );
  };

  if (!isLoaded || !state) return <Box p={3}>Loading Map...</Box>;

  return (
    <>
      {isDesktop && <Header />}
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {isDesktop && <NavBar selected='Vehicles' />}
        
        <Box sx={{ flexGrow: 1, position: 'relative', height: '100%', marginLeft: isDesktop ? '172px' : 0, marginTop: isDesktop ? '60px' : 0 }}>
          {(!isDesktop ) && (
            <IconButton 
              onClick={() => navigate(-1)} 
              sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  left: 12, 
                  zIndex: 20, 
                  background: 'linear-gradient(45deg, black, #ed1b1b)',
                  color: 'white', 
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <ArrowBackIcon sx={{ color: '#ffffff' }} />
            </IconButton>
          )}

          {viewMode === 'map' && !dayWiseData && (
            <Paper 
                elevation={4} 
                sx={{ 
                    position: 'absolute', 
                    top: 12, 
                    left: 64, 
                    right: 12, 
                    zIndex: 10, 
                    padding: '12px', 
                    borderRadius: '12px',
                    maxWidth: { md: '450px' },
                }}
            >
                <Box display="flex" flexDirection="column" gap={1.5}>
                   {/* --- START LOCATION INPUT --- */}
                    <Autocomplete onLoad={(a) => (originAutocompleteRef.current = a)} onPlaceChanged={() => {
                            const place = originAutocompleteRef.current?.getPlace();
                            if (place?.geometry?.location) {
                                const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
                                setOriginPos(newPos);
                                setOriginText(place.formatted_address || "");
                                setMapCenter(newPos);
                                handleMapClick(); 
                            }
                        }}>
                        <Box display="flex" alignItems="center" sx={{ bgcolor: '#e8f0fe', borderRadius: '8px', padding: '2px 12px' }}>
                            <NearMeIcon sx={{ fontSize: 20, color: '#1a73e8', mr: 1 }} />
                            <InputBase value={originText} onChange={(e) => setOriginText(e.target.value)} placeholder="Start location" fullWidth sx={{ fontSize: '0.9rem' }} />
                            {originText.length > 0 && (
                                <IconButton 
                                    size="small" 
                                    onClick={() => {
                                        setOriginText('');
                                        setDirections(null); 
                                    }}
                                    sx={{ p: '4px' }}
                                >
                                    <CloseIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                                </IconButton>
                            )}
                        </Box>
                    </Autocomplete>

                    {/* --- DESTINATION INPUT --- */}
                    <Autocomplete onLoad={(a) => (destAutocompleteRef.current = a)} onPlaceChanged={() => {
                            const place = destAutocompleteRef.current?.getPlace();
                            if (place?.geometry?.location) {
                                setDestPos({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
                                setDestText(place.formatted_address || "");
                                handleMapClick(); 
                            }
                        }}>
                        <Box display="flex" alignItems="center" sx={{ bgcolor: '#fce8e6', borderRadius: '8px', padding: '2px 12px' }}>
                            <CircleIcon sx={{ fontSize: 14, color: '#d93025', mr: 1.5, ml: 0.5 }} />
                            <InputBase value={destText} onChange={(e) => setDestText(e.target.value)} placeholder="Destination" fullWidth sx={{ fontSize: '0.9rem' }} />
                        </Box>
                    </Autocomplete>
                </Box>
            </Paper>
          )}

          {dayWiseData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh' }}>
              {showPolyline && tripPath.length > 0 && (
                <Box sx={{ height: '30vh', width: '100%', flexShrink: 0, position: 'relative' }}>
                  <IconButton
                    onClick={() => {
                      setShowPolyline(false);
                      setTripPath([]);
                      setTripStops([]);
                      setTripStartPos(null);
                      setTripEndPos(null);
                      setCurrentRoutePos(null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 60,
                      left: 12,
                      zIndex: 9999, 
                      backgroundColor: '#ffffff',
                      color: '#5f6368', 
                      width: 40,
                      height: 40,
                      boxShadow: '0px 1px 4px rgba(0,0,0,0.3)',
                      '&:hover': { backgroundColor: '#f1f3f4' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <SharedGoogleMap 
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    mapCenter={mapCenter}
                    options={{ disableDefaultUI: true, fullscreenControl: true }}
                    onLoad={(m) => {
                      const bounds = new window.google.maps.LatLngBounds();
                      tripPath.forEach((point) => bounds.extend(point));
                      tripStops.forEach((stop) => bounds.extend({ lat: stop.lat, lng: stop.lng }));
                      m.fitBounds(bounds);
                    }}
                    showPolyline={true}
                    tripPath={tripPath}
                    tripStops={tripStops}
                    tripStartPos={tripStartPos}
                    tripEndPos={tripEndPos}
                    destPos={destPos}
                    currentRoutePos={currentRoutePos}
                  />
                </Box>
              )}
              
              <Box sx={{ flexGrow: 1, pt: showPolyline ? 2 : 10, px: 2, bgcolor: '#f5f5f5', overflowY: 'auto', pb: 25 }}>
                <Paper elevation={3} sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a73e8', fontWeight: 'bold' }}>
                    📅 Route Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f3f4' }}>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell align="right"><strong>Distance (km)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dayWiseData.map((row, index) => (
                          <TableRow 
                            key={index} 
                            hover
                            onClick={() => handleRowClick(row.date)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e8f0fe' } }}
                          >
                            <TableCell>{formatDateDDMMYYYY(row.date)}</TableCell>
                            <TableCell align="right">{row.distance_km} km</TableCell>
                          </TableRow>
                        ))}
                        {dayWiseData.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No travel data found for this range.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>

            </Box>
          ) : (
            <SharedGoogleMap 
              mapContainerStyle={containerStyle}
              mapCenter={mapCenter}
              options={{ 
                disableDefaultUI: true, 
                zoomControl: true,
                zoomControlOptions: { position: window.google?.maps?.ControlPosition?.RIGHT_CENTER }
              }}
              onLoad={setMap}
              directions={directions}
              showPolyline={showPolyline}
              tripPath={tripPath}
              tripStops={tripStops}
              tripStartPos={tripStartPos}
              tripEndPos={tripEndPos}
              originPos={originPos}
              destPos={destPos}
              currentRoutePos={currentRoutePos}
            />
          )}

          <Draggable nodeRef={dragRef} disabled={!isDesktop}>
            <Paper 
              ref={dragRef}
              elevation={6} 
              sx={{ 
                position: 'fixed', 
                bottom: isDesktop ? 'auto' : 0, 
                top: isDesktop ? 80 : 'auto', 
                left: isDesktop ? 'auto' : 0, 
                right: isDesktop ? '20px' : 0,
                width: isDesktop ? '400px' : '100%',
                padding: '12px', 
                borderRadius: isDesktop ? '16px' : '16px 16px 0 0',
                zIndex: 10,
                cursor: isDesktop ? 'grab' : 'default', 
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMinimized ? 0 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="primary">
                    {state?.vehicleNo}
                  </Typography>
                  {state?.driverName && (
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      • {state?.driverName}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => setIsMinimized(!isMinimized)} sx={{ p: 0.5 }}>
                  {isMinimized ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Collapse in={!isMinimized} timeout="auto" unmountOnExit>
                <Box sx={{ padding: '0px' }}>
                  {showDatePicker && (
                    <Stack direction="row" spacing={2} sx={{ marginBottom:'12px' }}>
                      <TextField 
                        label="From" 
                        type="datetime-local" 
                        value={fromDate} 
                        onChange={handleFromDateChange} 
                        onClick={handleInputClick}
                        fullWidth 
                        size="small" 
                        InputLabelProps={{ shrink: true }} 
                        sx={noCalendarIconStyle} 
                      />
                      <TextField 
                        label="To" 
                        type="datetime-local" 
                        value={toDate} 
                        onChange={handleToDateChange} 
                        onClick={handleInputClick}
                        fullWidth 
                        size="small" 
                        InputLabelProps={{ shrink: true }} 
                        sx={noCalendarIconStyle} 
                      />
                    </Stack>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      {(distance || duration) && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 0 }}>
                          {distance && (
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a73e8', fontSize: '15px' }}>
                              🚗 {distance}
                            </Typography>
                          )}
                          {duration && (
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a73e8', fontSize: '15px' }}>
                              ⏱ {duration}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {!isDesktop && (
                      <Box sx={{ display: 'flex', gap: '50px' }}>
                        {canEditVehicle && (
                            <FaFilePen onClick={() => handleEditClick(state?.vehicleList)} style={{ cursor: 'pointer', fontSize: '30px', color: '#1976d2' }} />
                        )}
                        <CallIcon onClick={() => window.location.href = `tel:${state.driverPhone}`} sx={{ color: '#4a81c5', cursor: 'pointer', fontSize: '30px' }} />
                      </Box>
                    )}
                  </Box>        

                  <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1 }}>
                    <Button variant="outlined" onClick={handleHistoryClick} startIcon={<FileClockOutline />} sx={getActiveStyle('history')}>
                      History
                    </Button>
                    <Button variant="outlined" onClick={handleMapClick} startIcon={<img src={mapsIcon} alt="Map" style={{ width: '24px', height: '24px' }} />} sx={getActiveStyle('map')}>
                      Directions
                    </Button>
                    <Button variant="outlined" onClick={handleTripsClick} startIcon={<ListAltIcon />} sx={getActiveStyle('trips')}>
                      Trips
                    </Button>
                    <Box sx={{ flexShrink: 0 }}>
                      {renderTripActionButton()}
                    </Box>        
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          </Draggable>
        </Box>

        <TripDetailsModal 
            isOpen={openTripModal} 
            onClose={() => { setOpenTripModal(false); fetchCurrentTrip(); }} 
            type={tripActionType} 
            tripId={state.tripId} 
            vehicleId={state.vehiclesId} 
            vehicleList={state.vehicleList || []} 
            orderPicked={state.ordersPicked} 
            orderDelivered={state.ordersDelivered} 
        />
        <EditVehicleModal
            open={openEditModal}
            onClose={handleCloseModal}
            vehicle={fetchedVehicle}
            tab={'active'}
        />
        <TripsListDrawer 
          open={openTripsDrawer}
          onClose={() => { 
              setOpenTripsDrawer(false); 
              if (directions) {
                  setViewMode('map');
              } else {
                  setViewMode('history');
                  setShowDatePicker(true);
              }
          }}
          vehicleId={state?.vehiclesId}
          defaultVehicleNo={state?.vehicleNo}
          defaultDriverName={state?.driverName}
          defaultDriverPhone={state?.driverPhone}
        />
      </Box>
    </>
  );
};

export default TripDirections;